import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component,
    EventEmitter, Input, Output, ViewChild,
} from '@angular/core';

import { DxcIconComponent } from '../icon/dxc-icon.component';
import { DxcStateIconComponent } from '../state-icon/dxc-state-icon.component';
import { DxcUploadComponent } from '../upload/dxc-upload.component';
import { DxcAttachment, DxcBadgeVariant, DxcIconName, DxcStateIconName } from '../dxc.types';
import { DXC_FILE_FORMAT_VARIANT, dxcFileFormat } from '../text/dxc-file-format';

/**
 * Panel de archivos adjuntos: la lista de lo que ya está, el hueco ilustrado
 * cuando no hay nada, y la superficie que acepta un archivo soltado.
 *
 * Existe porque el panel del borrador lo tenía compuesto a mano y la parte
 * importante no funcionaba. Tres defectos medidos, ninguno de gusto:
 *
 * · **El arrastre no llegaba a ninguna parte.** El panel escuchaba `dragover` y
 *   `drop`, pero el único aviso visible era el que NO daba: nada cambiaba al
 *   pasar el archivo por encima, así que la superficie no decía que aceptara
 *   nada. Es el defecto que R9 describe, con el agravante de que aquí el
 *   `drop` sí estaba escrito — lo que faltaba era decirlo.
 * · **El botón de añadir era `.dxc-dropzone` a secas**, es decir la caja con
 *   aspecto de zona de arrastre y el comportamiento de otra cosa: un `<input>`
 *   escondido y un `click()` a mano. `<dxc-upload>` ya resuelve eso —teclado,
 *   contador de profundidad, `accept` comprobado también al soltar— y estaba
 *   a un import de distancia.
 * · **El hueco vacío iba con `dxc-icon name="document"`**, el glifo genérico
 *   de 24 px estirado dentro de un chip de 44. Un vacío es una SITUACIÓN, y
 *   §6 le asigna la otra familia: `dxc-state-icon`, ilustrada y dibujada para
 *   el caso. De ahí `sin-adjuntos`.
 *
 * Decisiones propias:
 *
 * - **El panel entero es la zona de soltar, no solo la caja discontinua.**
 *   Quien arrastra un archivo apunta al bloque que dice «Archivos adjuntos»,
 *   no a una franja de 76 px al final. La caja sigue estando porque es la
 *   afordancia de pulsar, y el velo de acento cubre el panel al arrastrar.
 * - **El velo no intercepta el puntero** (`pointer-events:none`). Si lo
 *   hiciera, aparecer bajo el cursor dispararía `dragleave` del panel, el velo
 *   se apagaría, y volvería a entrar: parpadeo mientras se mueve por encima.
 * - **El `drop` que ya atendió `<dxc-upload>` no se atiende dos veces.** El
 *   evento nace en el hijo y burbujea hasta aquí; sin la comprobación de
 *   origen, soltar sobre la caja subía el archivo dos veces.
 * - **La extensión y su tono se deducen del nombre.** Si no, cada vista vuelve
 *   a escribir su propio mapa `pdf → rojo`.
 */
@Component({
    standalone: true,
    selector: 'dxc-attachments',
    imports: [DxcIconComponent, DxcStateIconComponent, DxcUploadComponent],
    templateUrl: './dxc-attachments.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcAttachmentsComponent {

    @Input() title = 'Archivos adjuntos';

    @Input() items: DxcAttachment[] = [];

    /** Mientras se leen los adjuntos: la forma de la lista, no un anillo. */
    @Input() loading = false;

    /** Subiendo o procesando. Bloquea la zona y explica por qué. */
    @Input() busy = false;

    @Input() busyLabel = 'Subiendo documento…';

    @Input() disabled = false;

    // --- Hueco vacío ---------------------------------------------------------

    @Input() emptyIcon: DxcStateIconName = 'sin-adjuntos';
    @Input() emptyTitle = 'Sin archivos adjuntos';
    @Input() emptyHint = '';

    // --- Zona de carga -------------------------------------------------------

    @Input() uploadLabel = 'Arrastra un archivo o pulsa para elegirlo';
    @Input() uploadHint = '';
    @Input() uploadIcon: DxcIconName = 'soltar-archivo';
    @Input() accept = '';
    @Input() multiple = false;
    /** La densidad apretada de `dxc-upload`, para un panel lateral estrecho. */
    @Input() compact = false;

    /** Lo que dice el velo mientras el archivo está encima. */
    @Input() dropLabel = 'Suelta el archivo para adjuntarlo';

    /** Glifo del chip de una fila que no trae el suyo. */
    @Input() itemIcon: DxcIconName = 'documento-adjunto';

    /**
     * Quitar un adjunto. Por fila lo decide `item.removable`, y este `@Input`
     * es el valor por defecto de la lista: en el borrador conviven los que
     * subió el usuario —que sí se quitan— con los que llegaron en el correo,
     * que son de dónde salió la carga y no se tocan.
     */
    @Input() removable = false;

    @Input() removeLabel = 'Quitar';

    /**
     * Reemplazar un adjunto por otro archivo. Como quitar, la primitiva solo emite: elegir el
     * archivo nuevo y subirlo es de la vista.
     */
    @Input() replaceable = false;

    @Input() replaceLabel = 'Reemplazar';

    @Output() filesSelected = new EventEmitter<File[]>();

    @Output() rejected = new EventEmitter<File[]>();

    /** Se pulsó una fila. Solo las que traen `url` son pulsables. */
    @Output() opened = new EventEmitter<DxcAttachment>();

    /**
     * Se pidió quitar un adjunto. La primitiva **no confirma ni borra**: quitar
     * es destructivo y quien sabe qué se pierde de verdad es la vista. Aquí
     * solo se emite.
     */
    @Output() removed = new EventEmitter<DxcAttachment>();

    /** Se pidió reemplazar un adjunto (solo con `[replaceable]`). */
    @Output() replaced = new EventEmitter<DxcAttachment>();

    @ViewChild('upload') private upload?: DxcUploadComponent;

    isOver = false;

    /** Tres huecos: la lista corta es el caso normal y cuatro ya mienten. */
    readonly skeletonRows = [0, 1, 2];

    /** Igual que el contador de `dxc-upload`, y por la misma razón. */
    #depth = 0;

    constructor(private cdr: ChangeDetectorRef) { }

    get isEmpty(): boolean {
        return !this.items?.length;
    }

    get canDrop(): boolean {
        return !this.disabled && !this.busy;
    }

    // El formato sale de `dxcFileFormat()`, el mismo que usa la ficha de
    // `.dxc-file-row`. Aquí vivía una segunda deducción, y fue la que se
    // desvió: tomaba el subtipo MIME tal cual como si fuera la extensión.
    badgeFor(item: DxcAttachment): string {
        return item.badge || dxcFileFormat(item)?.label || 'FILE';
    }

    variantFor(item: DxcAttachment): DxcBadgeVariant {
        if (item.badgeVariant) return item.badgeVariant;
        if (item.badge) return DXC_FILE_FORMAT_VARIANT[item.badge.toLowerCase()] ?? 'neutral';
        return dxcFileFormat(item)?.tone ?? 'neutral';
    }

    open(item: DxcAttachment): void {
        if (!item?.url) return;
        this.opened.emit(item);
    }

    canRemove(item: DxcAttachment): boolean {
        return item?.removable ?? this.removable;
    }

    remove(item: DxcAttachment): void {
        if (!this.canRemove(item) || this.busy || this.disabled) return;
        this.removed.emit(item);
    }

    replace(item: DxcAttachment): void {
        if (!this.replaceable || this.busy || this.disabled) return;
        this.replaced.emit(item);
    }

    // --- Arrastre sobre el panel entero --------------------------------------

    onDragEnter(event: DragEvent): void {
        if (!this.canDrop || !this.carriesFiles(event)) return;
        event.preventDefault();
        this.#depth += 1;
        if (!this.isOver) {
            this.isOver = true;
            this.cdr.markForCheck();
        }
    }

    onDragOver(event: DragEvent): void {
        if (!this.canDrop || !this.carriesFiles(event)) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    }

    onDragLeave(event: DragEvent): void {
        if (!this.canDrop) return;
        this.#depth = Math.max(0, this.#depth - 1);
        if (this.#depth === 0 && this.isOver) {
            this.isOver = false;
            this.cdr.markForCheck();
        }
    }

    onDrop(event: DragEvent): void {
        if (!this.canDrop) return;
        event.preventDefault();
        this.#depth = 0;
        this.isOver = false;
        this.cdr.markForCheck();

        // El evento nace en `<dxc-upload>` y burbujea hasta aquí. Si vino de
        // ahí ya está atendido —con su filtro de `accept` y todo—, así que
        // reenviarlo subiría el mismo archivo dos veces.
        const target = event.target as HTMLElement | null;
        if (target?.closest?.('.dxc-dropzone')) return;

        this.upload?.handleFiles([...(event.dataTransfer?.files ?? [])]);
    }

    /** Arrastrar texto o un enlace no es adjuntar: el velo no debe encenderse. */
    private carriesFiles(event: DragEvent): boolean {
        const types = event.dataTransfer?.types;
        return !types || Array.from(types).includes('Files');
    }
}
